import gsap from 'gsap';
import { Draggable } from 'gsap/Draggable';
import { InertiaPlugin } from 'gsap/InertiaPlugin';
gsap.registerPlugin( Draggable, InertiaPlugin );

export class Slides {
	constructor( selector, options = {} ) {
		this.elements = document.querySelectorAll( selector );
		this.options = options;
		this.paginationDots = [];
		this.isEnable = false;
		this.init();
	}

	init() {
		if ( this.isEnable ) return;
		if ( ! this.elements?.length ) return;
		this.execute();
		this.isEnable = true;
	}

	execute() {
		this.elements.forEach( ( container ) => {
			const theWrap = container.querySelector( '.el-container' );
			if ( ! theWrap ) return;
			const thePagination = container.querySelector( '.el-pagination' );
			const theToggles = container.querySelector( '.el-toggle-group' );
			const slides = gsap.utils.toArray( '.el-item', theWrap );
			const numSlides = slides.length;
			const stayTime = this.options.stay || 3;
			const duration = this.options.duration || 0.3;
			const loop = this.horizontalLoop( slides, {
				paused: true,
				paddingRight: 24,
				draggable: true,
			} );
			const timer = gsap.delayedCall( stayTime, () => {
				loop.next( { duration, ease: 'power1.inOut' } );
				timer.restart( true );
			} );
			if ( thePagination ) {
				this.paginationDots = Array.from(
					{ length: numSlides },
					( _, i ) => {
						const paginationDot = document.createElement( 'span' );
						if ( ! i ) {
							paginationDot.className =
								'el-pagination-dot current';
						} else {
							paginationDot.className = 'el-pagination-dot';
						}
						thePagination.appendChild( paginationDot );
						paginationDot.addEventListener( 'click', () => {
							timer.restart( true );
							loop.toIndex( i, {
								duration,
								ease: 'power1.inOut',
							} );
						} );
						return paginationDot;
					}
				);
			}
			if ( theToggles ) {
				const thePrev = theToggles.querySelector( '.el-toggle-prev' );
				const theNext = theToggles.querySelector( '.el-toggle-next' );
				if ( thePrev ) {
					thePrev.addEventListener( 'click', () => {
						timer.restart( true );
						loop.previous( { duration, ease: 'power1.inOut' } );
					} );
				}
				if ( theNext ) {
					theNext.addEventListener( 'click', () => {
						timer.restart( true );
						loop.next( { duration, ease: 'power1.inOut' } );
					} );
				}
			}
		} );
	}

	horizontalLoop( items, config ) {
		config = config || {};
		const onChange = config.onChange;
		let lastIndex = 0;
		const tl = gsap.timeline( {
				repeat: config.repeat,
				onUpdate:
					onChange &&
					function () {
						const i = tl.closestIndex();
						if ( lastIndex !== i ) {
							lastIndex = i;
							onChange( items[ i ], i );
						}
					},
				paused: config.paused,
				defaults: { ease: 'none' },
				onReverseComplete: () =>
					tl.totalTime( tl.rawTime() + tl.duration() * 100 ),
			} ),
			length = items.length,
			startX = items[ 0 ].offsetLeft,
			times = [],
			widths = [],
			spaceBefore = [],
			xPercents = [],
			center = config.center,
			pixelsPerSecond = ( config.speed || 1 ) * 100,
			snap =
				config.snap === false
					? ( v ) => v
					: gsap.utils.snap( config.snap || 1 );
		let timeOffset = 0;
		const theContainer =
			center === true
				? items[ 0 ].parentNode
				: gsap.utils.toArray( center )[ 0 ] || items[ 0 ].parentNode;
		let totalWidth = 0;
		const getTotalWidth = () =>
				items[ length - 1 ].offsetLeft +
				( xPercents[ length - 1 ] / 100 ) * widths[ length - 1 ] -
				startX +
				spaceBefore[ 0 ] +
				items[ length - 1 ].offsetWidth *
					gsap.getProperty( items[ length - 1 ], 'scaleX' ) +
				( parseFloat( config.paddingRight ) || 0 ),
			populateWidths = () => {
				let b1 = theContainer.getBoundingClientRect(),
					b2;
				items.forEach( ( el, i ) => {
					widths[ i ] = parseFloat(
						gsap.getProperty( el, 'width', 'px' )
					);
					xPercents[ i ] = snap(
						( parseFloat( gsap.getProperty( el, 'x', 'px' ) ) /
							widths[ i ] ) *
							100 +
							gsap.getProperty( el, 'xPercent' )
					);
					b2 = el.getBoundingClientRect();
					spaceBefore[ i ] = b2.left - ( i ? b1.right : b1.left );
					b1 = b2;
				} );
				gsap.set( items, {
					xPercent: ( i ) => xPercents[ i ],
				} );
				totalWidth = getTotalWidth();
			};
		let timeWrap = null,
			curIndex = 0,
			indexIsDirty = false;
		const populateOffsets = () => {
				timeOffset = center
					? ( tl.duration() * ( theContainer.offsetWidth / 2 ) ) /
					  totalWidth
					: 0;
				if ( center ) {
					times.forEach( ( t, i ) => {
						times[ i ] = timeWrap(
							tl.labels[ 'label' + i ] +
								( tl.duration() * widths[ i ] ) /
									2 /
									totalWidth -
								timeOffset
						);
					} );
				}
			},
			getClosest = ( values, value, wrap ) => {
				let i = values.length,
					closest = 1e10,
					index = 0,
					d;
				while ( i-- ) {
					d = Math.abs( values[ i ] - value );
					if ( d > wrap / 2 ) {
						d = wrap - d;
					}
					if ( d < closest ) {
						closest = d;
						index = i;
					}
				}
				return index;
			},
			populateTimeline = () => {
				let i, item, curX, distanceToStart, distanceToLoop;
				tl.clear();
				for ( i = 0; i < length; i++ ) {
					item = items[ i ];
					curX = ( xPercents[ i ] / 100 ) * widths[ i ];
					distanceToStart =
						item.offsetLeft + curX - startX + spaceBefore[ 0 ];
					distanceToLoop =
						distanceToStart +
						widths[ i ] * gsap.getProperty( item, 'scaleX' );
					tl.to(
						item,
						{
							xPercent: snap(
								( ( curX - distanceToLoop ) / widths[ i ] ) *
									100
							),
							duration: distanceToLoop / pixelsPerSecond,
						},
						0
					)
						.fromTo(
							item,
							{
								xPercent: snap(
									( ( curX - distanceToLoop + totalWidth ) /
										widths[ i ] ) *
										100
								),
							},
							{
								xPercent: xPercents[ i ],
								duration:
									( curX -
										distanceToLoop +
										totalWidth -
										curX ) /
									pixelsPerSecond,
								immediateRender: false,
							},
							distanceToLoop / pixelsPerSecond
						)
						.add( 'label' + i, distanceToStart / pixelsPerSecond );
					times[ i ] = distanceToStart / pixelsPerSecond;
				}
				timeWrap = gsap.utils.wrap( 0, tl.duration() );
			},
			refresh = ( deep ) => {
				const progress = tl.progress();
				tl.progress( 0, true );
				populateWidths();
				if ( deep ) populateTimeline();
				populateOffsets();
				if ( deep && tl.draggable ) {
					tl.time( times[ curIndex ], true );
				} else {
					tl.progress( progress, true );
				}
			};
		let proxy = null;
		gsap.set( items, { x: 0 } );
		populateWidths();
		populateTimeline();
		populateOffsets();
		window.addEventListener( 'resize', () => refresh( true ) );
		function toIndex( index, vars ) {
			vars = vars || {};
			if ( Math.abs( index - curIndex ) > length / 2 ) {
				index += index > curIndex ? -length : length;
			}
			const newIndex = gsap.utils.wrap( 0, length, index );
			let time = times[ newIndex ];
			if ( time > tl.time() !== index > curIndex && index !== curIndex ) {
				time += tl.duration() * ( index > curIndex ? 1 : -1 );
			}
			if ( time < 0 || time > tl.duration() ) {
				vars.modifiers = { time: timeWrap };
			}
			curIndex = newIndex;
			vars.overwrite = true;
			gsap.killTweensOf( proxy );
			return vars.duration === 0
				? tl.time( timeWrap( time ) )
				: tl.tweenTo( time, vars );
		}
		tl.toIndex = ( index, vars ) => {
			toIndex( index, vars );
			this.updateCurrent( index );
		};
		tl.closestIndex = ( setCurrent ) => {
			const index = getClosest( times, tl.time(), tl.duration() );
			if ( setCurrent ) {
				curIndex = index;
				indexIsDirty = false;
			}
			return index;
		};
		tl.current = () =>
			indexIsDirty ? tl.closestIndex( true ) : curIndex;
		tl.next = ( vars ) => {
			toIndex( tl.current() + 1, vars );
			this.updateCurrent( tl.current() );
		};
		tl.previous = ( vars ) => {
			toIndex( tl.current() - 1, vars );
			this.updateCurrent( tl.current() );
		};
		tl.times = times;
		tl.progress( 1, true ).progress( 0, true );
		if ( config.reversed ) {
			tl.vars.onReverseComplete();
			tl.reverse();
		}
		if ( config.draggable && typeof Draggable === 'function' ) {
			proxy = document.createElement( 'div' );
			const wrap = gsap.utils.wrap( 0, 1 );
			let ratio, startProgress, lastSnap, initChangeX;
			const align = () =>
					tl.progress(
						wrap(
							startProgress +
								( draggable.startX - draggable.x ) * ratio
						)
					),
				syncIndex = () => tl.closestIndex( true );
			const draggable = Draggable.create( proxy, {
				trigger: items[ 0 ].parentNode,
				type: 'x',
				onPressInit() {
					const x = this.x;
					gsap.killTweensOf( tl );
					startProgress = tl.progress();
					refresh();
					ratio = 1 / totalWidth;
					initChangeX = startProgress / -ratio - x;
					gsap.set( proxy, { x: startProgress / -ratio } );
				},
				onDrag: align,
				onThrowUpdate: align,
				overshootTolerance: 0,
				inertia: true,
				snap( value ) {
					if ( Math.abs( startProgress / -ratio - this.x ) < 10 ) {
						return lastSnap + initChangeX;
					}
					const time = -( value * ratio ) * tl.duration(),
						wrappedTime = timeWrap( time ),
						snapTime =
							times[
								getClosest( times, wrappedTime, tl.duration() )
							];
					let dif = snapTime - wrappedTime;
					if ( Math.abs( dif ) > tl.duration() / 2 ) {
						dif += dif < 0 ? tl.duration() : -tl.duration();
					}
					lastSnap = ( time + dif ) / tl.duration() / -ratio;
					return lastSnap;
				},
				onRelease() {
					syncIndex();
					if ( draggable.isThrowing ) indexIsDirty = true;
				},
				onThrowComplete: syncIndex,
			} )[ 0 ];
			tl.draggable = draggable;
		}
		tl.closestIndex( true );
		lastIndex = curIndex;
		onChange?.( items[ curIndex ], curIndex );
		return tl;
	}

	updateCurrent( index ) {
		this.paginationDots.forEach( ( dot, i ) => {
			dot.classList.toggle( 'current', i === index );
		} );
	}
}
